import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';
import { HeaderStyleGate } from '@theme/header-styles';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLDetailsElement | null} */
  #escapeDetails = null;
  #styles = new HeaderStyleGate(this);
  #openAttempt = 0;

  get headerStylesReady() { return this.#styles.ready; }

  // Called before the shared native-details toggle listener locks page scroll.
  prepareNativeOpen(details) {
    if (this.#styles.ready || !this.contains(details)) return;
    details.open = false;
    this.open(undefined, { target: details });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#styles.connect();

    this.addEventListener('keydown', this.#captureEscape, true);
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // Native details can open before this custom element upgrades. Preserve that
    // intent, but keep the panel closed until its deferred stylesheet is ready.
    if (this.refs.details.open) this.prepareNativeOpen(this.refs.details);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#styles.disconnect();
    this.#openAttempt++;
    this.removeEventListener('keydown', this.#captureEscape, true);
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#escapeDetails = null;
  }

  /**
   * Remember the open level before a nested accordion handles Escape on keydown.
   * @param {KeyboardEvent} event
   */
  #captureEscape = (event) => {
    if (event.key !== 'Escape' || event.isComposing || event.repeat) return;
    if (this.#styles.pending) { this.#styles.cancel(); return; }

    const details = this.#getDetailsElement(event).closest('details[open]');
    this.#escapeDetails = details instanceof HTMLDetailsElement && this.contains(details) ? details : null;
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const details = this.#escapeDetails;
    this.#escapeDetails = null;
    // A nested accordion may already have closed this level on keydown.
    if (event.isComposing || !details?.open) return;

    this.#close(details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    if (this.#styles.pending) {
      event?.preventDefault();
      this.#styles.cancel();
      return;
    }
    if (!this.isOpen && !this.#styles.ready) {
      event?.preventDefault();
      return this.open(undefined, event);
    }
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;
    if (!this.#styles.ready) {
      event?.preventDefault?.();
      details.open = false;
      this.#styles.run(() => {
        details.open = true;
        this.open(target, { target: details });
      });
      return;
    }

    const attempt = ++this.#openAttempt;
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (attempt !== this.#openAttempt || !this.isConnected) return;
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const wasPending = this.#styles.pending;
    this.#styles.cancel();
    const attempt = ++this.#openAttempt;
    if (wasPending && !details.open) return;
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (attempt !== this.#openAttempt || !this.isConnected) return;
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          summary.focus({ preventScroll: true });
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
